// @ts-check
// Aceite de convite (/invite/:token, equivalente ao /invite/:workspaceInviteHash do Twenty, doc 12, seção 11.1): mostra
// quem convidou e para qual instituição, pede o nome e confirma o e-mail convidado com o código de 6 dígitos do login.
// O link público de convite (Configurações > Segurança) usa a mesma tela, com o e-mail digitado pela pessoa.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { requestSignInEmailCode, verifySignInEmailCode } from '../../data/rest.js';
import { fetchInvitation } from '../../data/team.js';
import { ApiError, getErrorMessage } from '../../data/api-client.js';
import { MainButton } from '../../ui/button.js';
import { Skeleton } from '../../ui/feedback.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { Link } from '../../app/router.js';
import { AuthLayout, AuthTextField } from './auth-layout.js';
import { EMAIL_CODE_LENGTH, EmailCodeField, RESEND_COOLDOWN_SECONDS, ResendCodeButton, useCooldownSeconds } from './email-code-field.js';
import { t } from '../../i18n/index.js';

/**
 * @param {{ token: string, onAccepted: () => void }} props
 */
export function InviteAcceptPage({ token, onAccepted }) {
  const [invitation, setInvitation] = useState(/** @type {Awaited<ReturnType<typeof fetchInvitation>> | null | undefined} */ (undefined));
  const [step, setStep] = useState(/** @type {'details' | 'code'} */ ('details'));
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [typedEmail, setTypedEmail] = useState('');
  const [code, setCode] = useState('');
  const [isDevCode, setIsDevCode] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [codeError, setCodeError] = useState(/** @type {string | null} */ (null));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendAvailableAt, setResendAvailableAt] = useState(/** @type {number | null} */ (null));
  const resendSecondsLeft = useCooldownSeconds(resendAvailableAt);

  useEffect(() => {
    document.title = t('team.invite.documentTitle');
    fetchInvitation(token)
      .then((body) => setInvitation(/** @type {any} */ (body)?.data ?? body))
      .catch(() => setInvitation(null));
  }, [token]);

  const isPublicLink = invitation?.kind === 'PUBLIC_LINK';
  // no link público, o e-mail é o que a pessoa digitou e o token do link vai junto para o servidor liberar o código
  const email = isPublicLink ? typedEmail.trim() : invitation?.email ?? '';
  const linkInput = isPublicLink ? { inviteToken: token } : {};

  /** @param {{ devCode?: string }} response */
  const applyCodeResponse = (response) => {
    setResendAvailableAt(Date.now() + RESEND_COOLDOWN_SECONDS * 1000);
    if (response?.devCode) {
      setCode(response.devCode);
      setIsDevCode(true);
    }
  };

  const sendCode = async (/** @type {Event} */ event) => {
    event.preventDefault();
    if (!invitation) return;
    setError(null);
    setIsSubmitting(true);
    try {
      applyCodeResponse(await requestSignInEmailCode({ email, ...linkInput }));
      setStep('code');
    } catch (sendError) {
      setError(getErrorMessage(sendError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resendCode = async () => {
    if (!invitation) return;
    setIsResending(true);
    try {
      applyCodeResponse(await requestSignInEmailCode({ email, ...linkInput }));
      showSuccessSnackbar(t('auth.emailCode.resent', { email }));
    } catch (sendError) {
      setError(getErrorMessage(sendError));
    } finally {
      setIsResending(false);
    }
  };

  /** @param {string} value */
  const verifyCode = async (value) => {
    if (!invitation || value.length !== EMAIL_CODE_LENGTH || isSubmitting) return;
    setError(null);
    setCodeError(null);
    setIsSubmitting(true);
    try {
      await verifySignInEmailCode({ email, code: value, firstName: firstName.trim(), lastName: lastName.trim(), ...linkInput });
      onAccepted();
    } catch (verifyError) {
      if (verifyError instanceof ApiError && verifyError.code === 'INVALID_EMAIL_CODE') {
        setCodeError(getErrorMessage(verifyError));
        setCode('');
        setIsDevCode(false);
      } else {
        setError(getErrorMessage(verifyError));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (invitation === undefined) {
    return html`<${AuthLayout} title=${t('team.invite.loading')}>
      <div class="auth-form"><${Skeleton} height=${40} radius=${8} /><${Skeleton} height=${40} radius=${8} /></div>
    <//>`;
  }

  const footer = html`<span>${t('team.invite.alreadyMember')} <${Link} href="/welcome">${t('team.invite.signIn')}<//></span>`;
  if (!invitation || invitation.status !== 'PENDING') {
    const status = invitation?.status ?? 'NOT_FOUND';
    return html`<${AuthLayout} title=${t(`team.invite.unavailable.${status}.title`)} subtitle=${t(`team.invite.unavailable.${status}.subtitle`)} tenant=${invitation?.tenant} footer=${footer}>
      <div class="auth-form"><${MainButton} title=${t('team.invite.goToSignIn')} variant="secondary" onClick=${() => window.location.assign('/welcome')} /></div>
    <//>`;
  }

  const institutionName = invitation.tenant.displayName;
  if (step === 'code') {
    return html`<${AuthLayout}
      title=${t('auth.signIn.codeTitle')}
      subtitle=${html`${t('auth.signIn.codeSubtitle')} <strong class="auth-subtitle-strong">${email}</strong>`}
      tenant=${invitation.tenant}
    >
      <form class="auth-form" onSubmit=${(/** @type {Event} */ event) => {
        event.preventDefault();
        verifyCode(code);
      }}>
        <${EmailCodeField}
          value=${code}
          onInput=${(value) => {
            setCode(value);
            setIsDevCode(false);
            if (codeError) setCodeError(null);
          }}
          onComplete=${verifyCode}
          error=${codeError}
          hint=${isDevCode ? t('auth.emailCode.devCodeHint') : t('auth.emailCode.hint')}
          disabled=${isSubmitting}
        />
        ${error ? html`<div class="auth-error" role="alert">${error}</div>` : null}
        <${MainButton} type="submit" title=${t('team.invite.accept')} isLoading=${isSubmitting} disabled=${code.length !== EMAIL_CODE_LENGTH} />
        <div class="auth-secondary-actions">
          <${ResendCodeButton} secondsLeft=${resendSecondsLeft} isSending=${isResending} onResend=${resendCode} />
          <button type="button" class="auth-link-button" onClick=${() => setStep('details')}>${t('common.back')}</button>
        </div>
      </form>
    <//>`;
  }

  return html`<${AuthLayout}
    title=${t('team.invite.title', { name: institutionName })}
    subtitle=${isPublicLink
      ? t('workspaceSettings.publicInvite.subtitle', { name: institutionName })
      : invitation.inviterName
        ? t('team.invite.subtitleWithInviter', { inviter: invitation.inviterName, name: institutionName })
        : t('team.invite.subtitle', { name: institutionName })}
    tenant=${invitation.tenant}
    footer=${footer}
  >
    <form class="auth-form" onSubmit=${sendCode}>
      ${isPublicLink
        ? html`<${AuthTextField} label=${t('auth.fields.email')} name="email" type="email" value=${typedEmail} onInput=${setTypedEmail}
            placeholder=${t('auth.fields.emailPlaceholder')} autoComplete="email" autoFocus=${true} />`
        : html`<label class="auth-field">
            <span class="input-label">${t('auth.fields.email')}</span>
            <input class="text-input auth-text-input" type="email" value=${invitation.email} readonly aria-readonly="true" />
          </label>`}
      <div class="auth-field-row">
        <${AuthTextField} label=${t('auth.fields.firstName')} name="firstName" value=${firstName} onInput=${setFirstName} autoComplete="given-name" autoFocus=${!isPublicLink} required=${false} />
        <${AuthTextField} label=${t('auth.fields.lastName')} name="lastName" value=${lastName} onInput=${setLastName} autoComplete="family-name" required=${false} />
      </div>
      <span class="input-hint">${t('team.invite.nameHint')}</span>
      ${error ? html`<div class="auth-error" role="alert">${error}</div>` : null}
      <${MainButton} type="submit" title=${t('auth.signIn.sendCode')} isLoading=${isSubmitting} disabled=${isPublicLink && !typedEmail.trim()} />
    </form>
  <//>`;
}
